import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import * as Matter from 'matter-js';

import ChartIntroLabel from '../ChartIntroLabel/ChartIntroLabel';

import useWindowWidth from '../../hooks/useWindowWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

import s from './GravityBallStats.module.scss';

const MIN_BALL_SIZE_VW = 9;
const MAX_BALL_SIZE_VW = 23;

const MIN_BALL_SIZE_RESP_VW = 20;
const MAX_BALL_SIZE_RESP_VW = 40;

const MATTER_INTERNAL_SIZE_DOM_SIZE_RATIO = 0.485;

const USE_DEBUG_RENDERER = false;

const GravityBallStats = (props) => {
  const {
    title,
    description,
    infoTitle,
    infoText,
    infoLink,
    items,
    centerGravityStrength,
    pointerForceStrength,
    textTheme,
  } = props;

  const [inViewRef, isVisible] = useIntersectionObserver(0.3, undefined, true);

  const ballContainerRef = useRef(null);
  const ballRefs = useRef([]);

  const mediumMq = useMediaQuery(`(min-width: 1024px)`);

  const windowWidth = useWindowWidth();

  useEffect(() => {
    if (!ballRefs.current || !ballContainerRef.current || !isVisible) {
      return;
    }

    // dimensions
    const containerWidth = ballContainerRef.current.offsetWidth;
    const containerHeight = ballContainerRef.current.offsetHeight;

    const containerMiddleW = containerWidth / 2;
    const containerMiddleH = containerHeight / 2;

    const maxBallSize = mediumMq ? MAX_BALL_SIZE_VW : MAX_BALL_SIZE_RESP_VW;
    const minBallSize = mediumMq ? MIN_BALL_SIZE_VW : MIN_BALL_SIZE_RESP_VW;

    // format plot data
    const maxPlotValue = items
      .map((b) => Math.abs(b.value))
      .reduce((max, v) => Math.max(max, v), -Infinity);

    const ballData = items.map((item, i) => {
      const percentage = item.value / (maxPlotValue * 0.55);
      const ballSizeVw = Math.min(
        Math.max(minBallSize, percentage * maxBallSize),
        maxBallSize
      );

      // const ballSizePx = window.innerWidth * (ballSizeVw / 100);
      const ballSizePx = containerWidth * (ballSizeVw / 100);
      const ballSizePxHalf = ballSizePx / 2;

      const xPos = Math.random() * containerWidth;
      const yPos = Math.random() * containerHeight;

      const body = Matter.Bodies.circle(
        xPos,
        yPos,
        ballSizePx * MATTER_INTERNAL_SIZE_DOM_SIZE_RATIO,
        {
          mass: 10,
        }
      );

      return {
        ...item,
        percentage,
        el: ballRefs.current[i],
        ballSizeVw,
        body,
        ballSizePx,
        ballSizePxHalf,
      };
    });

    // create an engine
    const engine = Matter.Engine.create({
      gravity: {
        x: 0,
        y: 0,
      },
      enableSleeping: true,
      constraintIterations: 1, // defeault: 2
      positionIterations: 1, // default: 6
      velocityIterations: 1, // default: 4
    });

    // create a matter renderer if debugging
    const render = USE_DEBUG_RENDERER
      ? Matter.Render.create({
          element: ballContainerRef.current,
          engine: engine,
          options: {
            width: containerWidth,
            height: containerHeight,
            showVelocity: true,
            showAngleIndicator: true,
            showStats: true,
            showPerformance: true,
          },
        })
      : null;

    // add mouse control
    const mouse = Matter.Mouse.create(ballContainerRef.current);
    const mouseConstraint = Matter.MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 0.1,
        render: {
          visible: false,
        },
      },
    });

    // fix scroll in ball container
    mouseConstraint.mouse.element.removeEventListener(
      'mousewheel',
      mouseConstraint.mouse.mousewheel
    );
    mouseConstraint.mouse.element.removeEventListener(
      'DOMMouseScroll',
      mouseConstraint.mouse.mousewheel
    );

    // fix touch "scroll" in ball container
    // see: https://github.com/liabru/matter-js/issues/678
    mouseConstraint.mouse.element.removeEventListener(
      'touchstart',
      mouseConstraint.mouse.mousedown
    );
    mouseConstraint.mouse.element.removeEventListener(
      'touchmove',
      mouseConstraint.mouse.mousemove
    );
    mouseConstraint.mouse.element.removeEventListener(
      'touchend',
      mouseConstraint.mouse.mouseup
    );

    mouseConstraint.mouse.element.addEventListener(
      'touchstart',
      mouseConstraint.mouse.mousedown,
      { passive: true }
    );
    mouseConstraint.mouse.element.addEventListener('touchmove', (e) => {
      if (mouseConstraint.body) {
        mouseConstraint.mouse.mousemove(e);
      }
    });
    mouseConstraint.mouse.element.addEventListener('touchend', (e) => {
      if (mouseConstraint.body) {
        mouseConstraint.mouse.mouseup(e);
      }
    });

    // walls
    const wallWidth = 10;

    Matter.Composite.add(engine.world, [
      Matter.Bodies.rectangle(
        containerWidth / 2,
        0,
        containerWidth,
        wallWidth,
        {
          isStatic: true,
        }
      ),
      Matter.Bodies.rectangle(
        containerWidth / 2,
        containerHeight,
        containerWidth,
        wallWidth,
        { isStatic: true }
      ),
      Matter.Bodies.rectangle(
        containerWidth,
        containerHeight / 2,
        wallWidth,
        containerHeight,
        { isStatic: true }
      ),
      Matter.Bodies.rectangle(
        0,
        containerHeight / 2,
        wallWidth,
        containerHeight,
        {
          isStatic: true,
        }
      ),
    ]);

    // add balls
    Matter.Composite.add(
      engine.world,
      ballData.map((b) => b.body)
    );

    const runner = Matter.Runner.create({
      delta: 1000 / 60, // default 1000 / 60
      isFixed: true,
      enabled: true,
    });

    Matter.Runner.run(runner, engine);

    let ballsIntersectedByPointer = [];
    let prevMousePos = { ...mouseConstraint.mouse.position };

    Matter.Events.on(engine, 'afterUpdate', (event) => {
      const mouse = mouseConstraint.mouse;
      const bodies = Matter.Composite.allBodies(engine.world).filter(
        (b) => !b.isStatic
      );

      const startPoint = mouse.position || { x: 0, y: 0 };
      const endPoint = { x: startPoint.x + 1, y: startPoint.y + 1 };

      // apply force in the direction of movement on pointer ball hover
      ballsIntersectedByPointer = Matter.Query.ray(
        bodies,
        startPoint,
        endPoint
      );

      ballsIntersectedByPointer.forEach((b) => {
        const body = b.body;
        const forceMagnitude = body.mass * 0.0133 * pointerForceStrength;

        const dirVec = Matter.Vector.mult(
          Matter.Vector.normalise(
            Matter.Vector.create(
              mouse.position.x - prevMousePos.x,
              mouse.position.y - prevMousePos.y
            )
          ),
          forceMagnitude
        );

        Matter.Body.applyForce(body, body.position, dirVec);
        prevMousePos = { ...mouseConstraint.mouse.position };
      });

      // trigger force on each ball to make it gravitate towards middle of world
      ballData.forEach((b) => {
        const body = b.body;

        const forceMagnitude = 0.002 * body.mass * centerGravityStrength;

        const vF = Matter.Vector.mult(
          Matter.Vector.normalise(
            Matter.Vector.create(
              containerMiddleW - body.position.x,
              containerMiddleH - body.position.y
            )
          ),
          forceMagnitude
        );

        Matter.Body.applyForce(body, body.position, vF);

        // update html el positions
        const xP = body.position.x - b.ballSizePxHalf;
        const yP = body.position.y - b.ballSizePxHalf;

        b.el.style = `
          transform: translateX(${xP}px) translateY(${yP}px);
          width: ${b.ballSizePx}px;
          height: ${b.ballSizePx}px;
        `;

        // b.el.style = `
        //   transform: translateX(${xP}px) translateY(${yP}px);
        //   width: ${b.ballSizeVw}vw;
        //   height: ${b.ballSizeVw}vw;
        // `;
      });
    });

    if (USE_DEBUG_RENDERER) {
      Matter.Render.run(render);
      Matter.Events.on(render, 'afterRender', function () {
        const mouse = mouseConstraint.mouse;
        const context = render.context;

        const endPoint = mouse.position || { x: 100, y: 600 };
        const startPoint = mouse.position || { x: 0, y: 0 };

        Matter.Render.startViewTransform(render);

        context.beginPath();
        context.moveTo(startPoint.x, startPoint.y);
        context.lineTo(endPoint.x, endPoint.y);

        if (ballsIntersectedByPointer.length > 0) {
          context.strokeStyle = '#fff';
        } else {
          context.strokeStyle = '#555';
        }

        context.lineWidth = 0.5;
        context.stroke();

        for (var i = 0; i < ballsIntersectedByPointer.length; i++) {
          var collision = ballsIntersectedByPointer[i];
          context.rect(
            collision.bodyA.position.x - 4.5,
            collision.bodyA.position.y - 4.5,
            8,
            8
          );
        }

        context.fillStyle = 'rgba(255, 165, 0, 0.7)';
        context.fill();

        Matter.Render.endViewTransform(render);
      });
    }

    return () => {
      Matter.World.clear(engine.world);
      Matter.Engine.clear(engine);

      if (render) {
        Matter.Render.stop(render);
        render.canvas.remove();

        render.canvas = null;
        render.context = null;
        render.textures = {};
      }
    };
  }, [isVisible, ballRefs, ballContainerRef, windowWidth]);

  const rootCls = classNames(s.Root, {
    [s.Visible]: isVisible,
    [s.Debug]: USE_DEBUG_RENDERER,
  });

  if (!items.length) {
    return null;
  }

  return (
    <div ref={inViewRef} className={rootCls}>
      <div className={`${s.TitleWrapper} grid`}>
        <div className={s.TitleContainer}>
          {title && (
            <ChartIntroLabel
              title={title}
              description={description}
              infoTitle={infoTitle}
              infoText={infoText}
              infoLink={infoLink}
              theme={textTheme}
            />
          )}
        </div>
      </div>
      <div ref={ballContainerRef} className={s.BallContainer}>
        {items.map((item, i) => {
          return (
            <div
              key={i}
              ref={(el) => (ballRefs.current[i] = el)}
              className={s.Ball}>
              <div className={s.BallContent}>
                <span className={s.BallLabel} id={`gravity-ball-label-${i}`}>
                  {item.label}
                </span>
                <span
                  className={s.BallValue}
                  aria-labelledby={`gravity-ball-label-${i}`}>
                  {item.valueLabel}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

GravityBallStats.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  infoTitle: PropTypes.string,
  infoText: PropTypes.string,
  infoLink: PropTypes.object,
  textTheme: PropTypes.oneOf(['light', 'dark']),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      valueLabel: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  centerGravityStrength: PropTypes.number,
  pointerForceStrength: PropTypes.number,
};

GravityBallStats.defaultProps = {
  textTheme: 'dark',
  items: [],
  centerGravityStrength: 0.14,
  pointerForceStrength: 0.34,
};

export default GravityBallStats;
